import { useState } from 'react';
import { getRoutesDirections } from '@/data/libraries/Routes';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { Location, NavigateFunction } from 'react-router-dom';
import _ from 'lodash';
import { Col, Container, Row } from 'react-bootstrap';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';
import { change } from '@/ui/store/slices/catalogFilterState';
import { changeBlockedByCheckoutState, changeLoginState, changeRecoveryState, changeRegisterState } from '@/ui/store/slices/showModalsSlice';

import './style.css';
import Icon from '@/ui/assets/Icon';

import CartWidget from '@/ui/components/sales/CartWidget';
import ModalMenuMobile from '@/ui/components/mobile/ModalMenuMobile';
import CartModal from '@/ui/components/modals/CartModal';


/**
 * Componente que se utiliza para mostrar el Nav de los usuarios
 * @component
 */

/** Componente que se utiliza para mostrar el Nav de los usuarios */
const NavMobile = (): JSX.Element => {
  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  /** Función para volver a la posición inicial */
  const scrollToZero = (): void => {
    window.scrollTo(0, 0);
  };

  /** Actualización de estado Redux */
  const dispatch = useAppDispatch();

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Estado inicial del modal para configuración de usuario */
  const [showOptionsUser, setShowOptionsUser] = useState<boolean>(false);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowOptionsUser = (): void => setShowOptionsUser(!showOptionsUser);

  /** Estado inicial del modal para configuración de usuario */
  const [showMenuMobile, setShowMenuMobile] = useState<boolean>(false);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowMenuMobile = (): void => setShowMenuMobile(!showMenuMobile);

  /** Estado inicial del modal de guía */
  const [showCartModal, setShowCartModal] = useState<boolean>(false);

  /**Estado de la modal de filtro */
  const showFilterModal: boolean = useAppSelector((state) => state.catalogFilterState.value);

  /**Estado de la modal de Login */
  const showLogin: boolean = useAppSelector((state) => state.showModals.showLogin);

  /** Evento que muestra y oculta el modal para login*/
  const handleShowLogin = (): void => {
    dispatch(changeLoginState());
  }

  /** Estado inicial del modal para Registro */
  const showRegister: boolean = useAppSelector((state) => state.showModals.showRegister);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowRegister = (): void => {
    dispatch(changeRegisterState());
  }

  /** Estado inicial del modal para Recobrar contraseña */
  const showRecovery: boolean = useAppSelector((state) => state.showModals.showRecovery);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowRecovery = (): void => {
    dispatch(changeRecoveryState());
  }

  /** Estado inicial del modal opciones de usuario */
  const showUserInfo: boolean = useAppSelector((state) => state.showModals.showUserInfo);

  /** Estado inicial del modal para codigo Recobrar contraseña */
  const showRecoveryCode: boolean = useAppSelector((state) => state.showModals.showRecoveryCode);

  /** Hook para generar una navegación dentro de la aplicación. */
  const navigate: NavigateFunction = useNavigate();
  /**Estado Redux para mostrar modal checkout */
  const showBlockedByCheckout: boolean = useAppSelector((state: RootState) => state.showModals.showBlockedByCheckout);
  /** Modifica el State Redux para modal checkout */
  const handleStatusmodalExitConfirmation = (): void => { dispatch(changeBlockedByCheckoutState()) };

  /** Función que muestra el modal de carrito */
  const handleShowCartModal = (): void => {
    if (location.pathname === routesDirections.CART_ROUTE || showBlockedByCheckout) return;
    if (location.pathname === routesDirections.CHECK_OUT_ROUTE) {
      !showBlockedByCheckout && handleStatusmodalExitConfirmation();
      navigate(routesDirections.CART_ROUTE);
      return;
    }
    if (!showCartModal && showLogin) {
      handleShowLogin();
    }
    if (!showCartModal && showRegister) {
      handleShowRegister();
    }
    if (!showCartModal && showRecovery) {
      handleShowRecovery();
    }

    // Cerrar cualquier modal FloatingButtonModal abierto
    // Intentar diferentes combinaciones de selectores para asegurar que encontramos el modal correcto
    const modalSelectors = [
      // Modal flotante móvil con clase bg-transparent
      '.modal.show .btn-icon-light[class*="CloseIcon"]',
      // Modal flotante desktop
      '.floating-slide-modal.show .btn-close',
      '.floating-slide-modal.show .btn-icon-light',
      // Modal basado en props
      '.modal.show[role="dialog"] .btn-close',
      '.modal.show[role="dialog"] .btn-icon-light'
    ];

    // Buscar todos los selectores hasta encontrar uno que funcione
    for (const selector of modalSelectors) {
      const closeButtons = document.querySelectorAll(selector);
      if (closeButtons.length > 0) {
        // Encontramos un botón de cierre, simulamos un clic
        (closeButtons[0] as HTMLElement).click();
        break; // Salir del bucle después de cerrar un modal
      }
    }

    // Cerrar directamente cualquier ButtonScrollTop modalizado
    const buttonScrollTopModals = document.querySelectorAll('[data-bs-backdrop="static"]');
    if (buttonScrollTopModals.length > 0) {
      // Si encontramos algún modal con este atributo, clausuramos
      const closeButtons = buttonScrollTopModals[0].querySelectorAll('.btn-close, [aria-label="Close"]');
      if (closeButtons.length > 0) {
        (closeButtons[0] as HTMLElement).click();
      }
    }

    if (!showCartModal && !showRecoveryCode && !showUserInfo) {
      setShowCartModal(!showCartModal);
    } else if (showCartModal) {
      setShowCartModal(!showCartModal);
    }
  };

  /** Función que cierra el modal de carrito */
  const closeModal = (): void => {
    setShowCartModal(false);
    /** Cierra el modal de filtro si esta activo */
    showFilterModal && dispatch(change(!showFilterModal));
  };

  /** Constante que define si es la ruta principal */
  const isMainRoute: boolean =
    !showCartModal && (location.pathname === routesDirections.MAIN_ROUTE || location.pathname === `${routesDirections.MAIN_ROUTE}/#`);

  /** Constante que define si es la ruta principal */
  const isCartOpen: boolean = location.pathname === routesDirections.CART_ROUTE || showCartModal;

  /** Indica el state de modal de bloqueo  */
  const showBlockedInCheckout: boolean = useAppSelector((state: RootState) => state.showModals.showBlockedInCheckout);
  /** Evita salir a home si esta el modal de bloqueo activo */
  const navLinkProps = showBlockedInCheckout
    ? {
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
      },
    }
    : {};

  /** Renderiza el componente de NavMobile */
  return (
    <>
      <Container fluid className='nav-mobile fixed-bottom text-center bg-primary-5 pt-2 ultra-top'>
        <Row>
          <Col xs={12} className='p-0'>
            <ul className='list-inline m-0 d-flex justify-content-between svg-secondary-1 align-items-center '>
              <Col xs={6}>
                <li className='px-2 '>
                  <NavLink
                    to={routesDirections.MAIN_ROUTE}
                    state={{ originURL: location.pathname }}
                    className={({ isActive }) =>
                      (isActive || isMainRoute) && !isCartOpen ? 'd-flex justify-content-center active' : 'd-flex justify-content-center'
                    }
                    {...navLinkProps}
                  >
                    <button
                      className='py-2 link-text-standard'
                      onClick={() => {
                        scrollToZero();
                        closeModal();
                      }}>
                      <Icon name='ico-home' />
                    </button>
                  </NavLink>
                </li>
              </Col>
              <Col xs={6}>
                <li className='px-2 '>
                  <a href="#" className={`d-flex justify-content-center ${isCartOpen ? 'active' : ''}`} onClick={(e) => e.preventDefault()}
                  >
                    <button className="py-2 link-text-standard" onClick={() => handleShowCartModal()}>
                      <CartWidget />
                    </button>
                  </a>
                </li>
              </Col>

              {/**  <NavLink to={'/favorites'} state={{ originURL: location.pathname }} className='flex-fill'>
              <li className='list-inline-item'>
                <FavoritesWidget classMain='py-2' />
              </li>
            </NavLink>*/}

              {/* <div onClick={!IsAOperMenu ? handleShowMenuMobile : undefined} className={`flex-fill ${IsAOperMenu && 'active'}`}>
              <li className='list-inline-item'>
                <div className='py-2'>
                  <Icon name='ico-menu' />
                </div>
              </li>
            </div> */}
            </ul>
          </Col>
        </Row>

        {showMenuMobile && (
          <ModalMenuMobile
            showMenuMobile={showMenuMobile}
            handleShowMenuMobile={handleShowMenuMobile}
            handleShowRegister={handleShowRegister}
            handleShowOptionsUser={handleShowOptionsUser}
          />
        )}
      </Container>

      {showCartModal && <CartModal handleClose={handleShowCartModal} />}
    </>
  );
};

export default NavMobile;
